import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../../hooks/useWallet';
import { CollapsibleSection } from '../shared/CollapsibleSection';
import { AgentIdentityEditor } from './AgentIdentityEditor';
import AgentResearchTab from '../research/AgentResearchTab';
import PortfolioPromptEditor from '../PortfolioPromptEditor';
import { PortfolioSettings } from '../PortfolioSettings';
import { HLAgentDebugPanel } from '../HLAgentDebugPanel';

function DangerousActivities({ agent, dangerConfirm, setDangerConfirm, dangerLoading, dangerError, setDangerError, onRemoveAllAgents, onDeleteWorkspace }) {
  return (
    <div className="border border-red-800/60 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4 bg-red-950/40">
        <span className="font-semibold text-red-400 text-sm">Dangerous Activities</span>
      </div>
      <div className="p-4 border-t border-red-800/40 flex flex-col gap-4">
        {dangerError && (
          <p className="text-xs text-red-400 bg-red-950/50 border border-red-700/50 rounded px-3 py-2">{dangerError}</p>
        )}

        {/* Remove all agents — only shown when an agent is assigned */}
        {agent && (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-slate-200 font-medium">Remove all agents</p>
                <p className="text-xs text-slate-500 mt-0.5">Unlinks all agent assignments from this workspace. The agents themselves are not deleted.</p>
              </div>
              {dangerConfirm === 'remove_agents' ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-red-300">Are you sure?</span>
                  <button onClick={onRemoveAllAgents} disabled={dangerLoading} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded text-xs text-white font-semibold transition-colors">
                    {dangerLoading ? 'Removing…' : 'Yes, remove'}
                  </button>
                  <button onClick={() => { setDangerConfirm(null); setDangerError(null); }} disabled={dangerLoading} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded text-xs text-slate-200 transition-colors">
                    Cancel
                  </button>
                </div>
              ) : (
                <button onClick={() => { setDangerConfirm('remove_agents'); setDangerError(null); }} className="flex-shrink-0 px-3 py-1.5 border border-red-700 text-red-400 hover:bg-red-900/40 rounded text-xs font-medium transition-colors">
                  Remove all agents
                </button>
              )}
            </div>
            <div className="border-t border-red-800/30" />
          </>
        )}

        {/* Delete workspace — always shown */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-slate-200 font-medium">Delete workspace</p>
            <p className="text-xs text-slate-500 mt-0.5">Permanently deletes this portfolio and all associated trades, positions, and history. This cannot be undone.</p>
          </div>
          {dangerConfirm === 'delete_workspace' ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-red-300">Are you sure?</span>
              <button onClick={onDeleteWorkspace} disabled={dangerLoading} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded text-xs text-white font-semibold transition-colors">
                {dangerLoading ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button onClick={() => { setDangerConfirm(null); setDangerError(null); }} disabled={dangerLoading} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded text-xs text-slate-200 transition-colors">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => { setDangerConfirm('delete_workspace'); setDangerError(null); }} className="flex-shrink-0 px-3 py-1.5 border border-red-700 text-red-400 hover:bg-red-900/40 rounded text-xs font-medium transition-colors">
              Delete workspace
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Unified Setup tab for workspace — merges agent config + portfolio settings.
 * Per wireframe wf-workspace.jsx SettingsBody: two columns on desktop,
 * stacked CollapsibleSections on mobile.
 */
export function WorkspaceSettings({ portfolio, agent, config, onAgentChange, onTokensChange }) {
  const { jwtToken } = useWallet();
  const navigate = useNavigate();
  const [dangerConfirm, setDangerConfirm] = useState(null); // 'remove_agents' | 'delete_workspace'
  const [dangerLoading, setDangerLoading] = useState(false);
  const [dangerError, setDangerError] = useState(null);
  const apiBase = config?.apiBaseUrl || 'http://localhost:8000';
  const headers = { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' };

  const handleRemoveAllAgents = async () => {
    setDangerLoading(true);
    setDangerError(null);
    try {
      const res = await fetch(`${apiBase}/portfolio/${portfolio.id}/agents`, { headers });
      const assignments = res.ok ? await res.json() : [];
      await Promise.all(
        assignments
          .filter(a => a.is_active)
          .map(a =>
            fetch(`${apiBase}/portfolio/${portfolio.id}/agent/${a.agent_id}`, {
              method: 'DELETE',
              headers,
            })
          )
      );
      setDangerConfirm(null);
      onAgentChange?.();
    } catch (err) {
      setDangerError(err.message);
    } finally {
      setDangerLoading(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    setDangerLoading(true);
    setDangerError(null);
    try {
      const res = await fetch(`${apiBase}/portfolio/${portfolio.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Delete failed (${res.status})`);
      }
      navigate('/dashboard');
    } catch (err) {
      setDangerError(err.message);
      setDangerLoading(false);
    }
  };

  if (!agent) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-12 h-12 text-slate-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-slate-400 text-sm">Assign an agent to configure workspace settings.</p>
        </div>
        <DangerousActivities
          agent={null}
          dangerConfirm={dangerConfirm}
          setDangerConfirm={setDangerConfirm}
          dangerLoading={dangerLoading}
          dangerError={dangerError}
          setDangerError={setDangerError}
          onRemoveAllAgents={handleRemoveAllAgents}
          onDeleteWorkspace={handleDeleteWorkspace}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Left column */}
      <div className="flex flex-col gap-4">
        <CollapsibleSection title="Agent Identity" defaultOpen={true}>
          <AgentIdentityEditor
            agent={agent}
            config={config}
            onSave={onAgentChange}
          />
        </CollapsibleSection>

        <CollapsibleSection title="Research Configuration" defaultOpen={true}>
          <AgentResearchTab
            agentId={agent.id}
            config={config}
            authToken={jwtToken}
          />
        </CollapsibleSection>
      </div>

      {/* Right column */}
      <div className="flex flex-col gap-4">
        <CollapsibleSection title="Recommendation Prompts" defaultOpen={true}>
          <PortfolioPromptEditor
            agentId={agent.id}
            config={config}
            authToken={jwtToken}
          />
        </CollapsibleSection>

        <CollapsibleSection title="Portfolio & Agent Link" defaultOpen={true}>
          <PortfolioSettings
            portfolioId={portfolio.id}
            config={config}
            onTokensChange={onTokensChange}
            onAgentChange={onAgentChange}
          />
        </CollapsibleSection>
      </div>
      {/* Hyperliquid Debug Panel — full width, only for Hyperliquid workspaces */}
      {portfolio?.portfolio_type === 'HYPERLIQUID' && (
        <div className="xl:col-span-2">
          <HLAgentDebugPanel
            config={config}
            authToken={jwtToken}
            portfolioId={portfolio.id}
            portfolio={portfolio}
            onBuilderFeeApproved={onAgentChange}
          />
        </div>
      )}
      {/* Dangerous Activities — full width below both columns */}
      <div className="xl:col-span-2">
        <DangerousActivities
          agent={agent}
          dangerConfirm={dangerConfirm}
          setDangerConfirm={setDangerConfirm}
          dangerLoading={dangerLoading}
          dangerError={dangerError}
          setDangerError={setDangerError}
          onRemoveAllAgents={handleRemoveAllAgents}
          onDeleteWorkspace={handleDeleteWorkspace}
        />
      </div>
    </div>
  );
}
